import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCounters } from '../../contexts/CounterContext';
import { useUser } from '../../contexts/UserContext';
import { useCopyUrl } from '../../hooks/useCopyUrl';
import { useToast } from '../../utils/toastUtils';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorAlert from '../common/ErrorAlert';
import ToastContainer from '../common/ToastContainer';
import CreateCounterModal from '../modals/CreateCounterModal';
import { Modal } from 'bootstrap';
import { Container, Card, Button, Table, Form, Badge } from 'react-bootstrap';
import { isCounterExpired } from '../../utils/dateUtils';
import { extractIdFromUrl } from '../../utils/urlUtils';
import { usePagination } from '../../hooks/usePagination';
import Pagination from '../common/Pagination';
import { config } from '../../config';

/**
 * CountersList component displays all counters with filtering capabilities.
 * 
 * This component is responsible for:
 * - Displaying all counters in a paginated table
 * - Allowing filtering between all counters and user-owned counters
 * - Providing navigation to individual counters
 * - Managing counter creation and deletion
 */

function CountersList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    counters, 
    loading, 
    error, 
    selectedCounterId, 
    addCounter, 
    removeCounter, 
    selectCounter,
    refreshCounters
  } = useCounters();
  const { isAdmin, user, isLoggedIn } = useUser();
  const { copyUrl, getCounterUrl } = useCopyUrl();
  const { toasts, showToast } = useToast();

  const [isCreating, setIsCreating] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState<string | null>(null);
  const modalRef = useRef<Modal | null>(null);
  const initialFetchRef = useRef(false);
  
  // Toggle for showing all counters or only user counters
  const [showOnlyMyCounters, setShowOnlyMyCounters] = useState(false);
  
  // Filter counters based on user type and toggle state
  const filteredCounters = React.useMemo(() => {
    let filtered = counters;
    
    // Filter private counters if user is not admin
    if (!isAdmin) {
      // Show private counters only if they belong to the logged-in user
      filtered = filtered.filter(counter => 
        !counter.isPrivate || (isLoggedIn && counter.ownerEmail === user.email)
      );
    }
    
    // If toggle is active and user is logged in, show only their counters
    if (showOnlyMyCounters && isLoggedIn && user.email) {
      filtered = filtered.filter(counter => counter.ownerEmail === user.email);
    }
    
    return filtered;
  }, [counters, isAdmin, showOnlyMyCounters, isLoggedIn, user.email]);

  // Use the usePagination hook with the appropriate config
  const { 
    currentPage, 
    paginate, 
    totalPages,
    itemsPerPage,
    paginationInfo,
    itemsPerPageOptions,
    setCurrentPage,
    handleItemsPerPageChange,
    getCurrentItems
  } = usePagination({
    totalItems: filteredCounters.length,
    defaultItemsPerPage: config.pagination.defaultItemsPerPage
  });
  
  // Get current page counters using the pagination hook
  const { currentItems: currentCounters } = getCurrentItems(filteredCounters);

  // Refresh data when component mounts or user navigates to this page
  useEffect(() => {
    // Only fetch counters once when the component mounts or when the URL path changes
    if (!initialFetchRef.current || location.pathname !== '/counters') {
      refreshCounters();
      initialFetchRef.current = true;
    }
  }, [location.pathname]);

  const openCreateModal = () => {
    const modalEl = document.getElementById('createCounterModal');
    if (modalEl) {
      const modal = new Modal(modalEl);
      modalRef.current = modal;
      modal.show();
    }
  };

  const handleCreateCounter = async (name: string, isPrivate: boolean = false, email?: string) => {
    try {
      setIsCreating(true);
      const newCounter = await addCounter(name, isPrivate, email);
      selectCounter(newCounter._id);
      
      // Hide the modal
      if (modalRef.current) {
        modalRef.current.hide();
      }
      
      navigate(`/${newCounter.ownerUrl}`);
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteCounter = async (counterId: string) => {
    try {
      await removeCounter(counterId);
      setIsConfirmingDelete(null);
      showToast(t('toasts.counterDeleted', 'Counter deleted successfully'), 'success');
    } catch (err) {
      console.error(t('errors.deleteError', 'Error deleting counter:'), err);
      showToast(t('toasts.deleteCounterFailed', 'Failed to delete counter'), 'danger');
    }
  };

  // Note: Do not return early on loading; render static layout and show loading only for the table

  return (
    <div className="container py-3 py-md-5">
      <div className="row justify-content-center">
        <div className="col-lg-10">
          {/* Header with Create Button */}
          <div className="text-center mb-4">
            <h1 className="h3 h1-md fw-bold mb-2">{t('counters.title', 'Counters')}</h1>
            <p className="small text-muted mb-3">
              {isLoggedIn 
                ? t('counters.descriptionLoggedIn', 'Below you can view all publicly available counters or filter to see only your own tap counters. Create and manage your counters with custom buttons for tracking.')
                : t('counters.descriptionPublic', 'This is a list of publicly available tap counters. You can browse existing counters or create your own. When creating a counter, you can set it to private so it doesn\'t appear in this public list.')
              }
            </p>
            <button 
              className="btn btn-primary"
              onClick={openCreateModal}
              aria-label={t('counters.createNewAriaLabel', 'Create a new counter')}
            >
              <i className="bi bi-plus-circle me-2" aria-hidden="true"></i>
              {t('counters.createNew', 'Create New Counter')}
            </button>
          </div>
          
          <ErrorAlert message={error} />
          


          {/* Counter List */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-body py-2 border-bottom border-opacity-10 d-flex justify-content-between align-items-center">
              <h5 className="mb-0 fs-6">{t('counters.yourCounters', 'Your Counters')}</h5>
              
              {/* Toggle for logged-in users - more prominent UI */}
              {isLoggedIn && (
                <div className="btn-group" role="group" aria-label={t('counters.filterOptions', 'Filter options')}>
                  <button 
                    type="button" 
                    className={`btn btn-sm ${!showOnlyMyCounters ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => { setShowOnlyMyCounters(false); setCurrentPage(1); }}
                  >
                    <i className="bi bi-globe me-1" aria-hidden="true"></i>
                    {t('counters.allCounters', 'All counters')}
                  </button>
                  <button 
                    type="button" 
                    className={`btn btn-sm ${showOnlyMyCounters ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => { setShowOnlyMyCounters(true); setCurrentPage(1); }}
                  >
                    <i className="bi bi-person me-1" aria-hidden="true"></i>
                    {t('counters.onlyMyCounters', 'Only my counters')}
                  </button>
                </div>
              )}
            </div>
            
            {loading ? (
              <div className="card-body p-3">
                <div className="d-flex justify-content-center py-4">
                  <LoadingSpinner size="lg" />
                </div>
              </div>
            ) : filteredCounters.length === 0 ? (
              <div className="card-body p-3 text-center">
                <div className="py-4">
                  <i className="bi bi-inbox text-body-secondary display-5 mb-3 d-block" aria-hidden="true"></i>
                  <h5 className="mb-2">{t('counters.noCounters', 'No counters yet')}</h5>
                  <p className="text-muted small mb-3">
                    {t('counters.createFirst', 'Create your first counter to get started')}
                  </p>
                  <button 
                    className="btn btn-primary btn-sm" 
                    onClick={openCreateModal}
                    aria-label={t('counters.createNewAriaLabel', 'Create a new counter')}
                  >
                    <i className="bi bi-plus-circle me-2" aria-hidden="true"></i>
                    {t('counters.createNew', 'Create New Counter')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover table-sm mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>{t('counters.name', 'Name')}</th>
                      <th className="d-none d-md-table-cell">{t('counters.created', 'Created')}</th>
                      <th className="d-none d-sm-table-cell">{t('counter.publicUrl', 'Public URL')}</th>
                      {isAdmin && (
                        <th className="d-none d-lg-table-cell">{t('counter.ownerUrl', 'Owner URL')}</th>
                      )}
                      <th className="text-end">{t('common.actions', 'Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentCounters.map((counter) => (
                      <tr 
                        key={counter._id}
                      >
                        <td 
                          className="align-middle fw-semibold cursor-pointer"
                          onClick={() => {
                            // Use owner URL for the user's own counters
                            const url = isLoggedIn && counter.ownerEmail === user.email
                              ? counter.ownerUrl
                              : counter._id;
                            navigate(`/${url}`);
                          }}
                          role="button"
                          aria-label={t('counters.openCounterAriaLabel', 'Open counter: {{name}}', { name: counter.name })}
                          title={counter.name}
                        >
                          <div className="text-truncate" style={{ maxWidth: "200px" }}>
                            {counter.name}
                          </div>
                          {counter.isPrivate && (
                            <span className="ms-2 badge bg-secondary" title={t('counters.privateCounterLabel', 'Private counter')}>
                              <i className="bi bi-eye-slash-fill me-1" aria-hidden="true"></i>
                              {t('counters.private', 'Private')}
                            </span>
                          )}
                          {isCounterExpired(counter) && (
                            <span className="ms-2 badge bg-danger" title={t('counters.expiredCounterLabel', 'Expired counter')}>
                              <i className="bi bi-x-circle-fill me-1" aria-hidden="true"></i>
                              {t('counters.expired', 'Expired')}
                            </span>
                          )}
                          {isLoggedIn && counter.ownerEmail === user.email && (
                            <span className="ms-2 badge bg-success" title={t('counters.myCounterLabel', 'My counter')}>
                              <i className="bi bi-person-check-fill me-1" aria-hidden="true"></i>
                              {t('counters.myCounter', 'My Counter')}
                            </span>
                          )}
                          {counter.hasPaidButtons && (
                            <span className="ms-2 badge bg-info" title={t('counters.paidCounterLabel', 'Has paid buttons')}>
                              <i className="bi bi-currency-dollar me-1" aria-hidden="true"></i>
                              {t('counters.paidCounter', 'Paid Counter')}
                            </span>
                          )}
                        </td>
                        <td className="align-middle d-none d-md-table-cell">
                          <small className="text-body-secondary">
                            <i className="bi bi-calendar-date me-1" aria-hidden="true"></i>
                            {new Date(counter.createdAt).toLocaleString()}
                          </small>
                        </td>
                        <td className="align-middle d-none d-sm-table-cell">
                          <div className="input-group input-group-sm">
                            <input 
                              type="text" 
                              className="form-control form-control-sm bg-body-subtle border border-opacity-25 text-body" 
                              value={getCounterUrl(counter._id, false)}
                              readOnly 
                              onClick={(e) => e.stopPropagation()}
                              aria-label={t('counter.publicUrlAriaLabel', 'Counter public URL')}
                            />
                            <button 
                              className="btn btn-outline-primary btn-sm" 
                              type="button" 
                              onClick={(e) => {
                                e.stopPropagation();
                                copyUrl(counter._id, false);
                              }}
                              aria-label={t('counter.copyButtonAriaLabel', 'Copy URL to clipboard')}
                            >
                              <i className="bi bi-clipboard" aria-hidden="true"></i>
                            </button>
                          </div>
                        </td>
                        {isAdmin && (
                        <td className="align-middle d-none d-lg-table-cell">
                          <div className="input-group input-group-sm">
                            <input 
                              type="text" 
                              className="form-control form-control-sm bg-body-subtle border border-opacity-25 text-body" 
                              value={getCounterUrl(counter.ownerUrl, true)}
                              readOnly 
                              onClick={(e) => e.stopPropagation()}
                              aria-label={t('counter.ownerUrlAriaLabel', 'Counter owner URL')}
                            />
                            <button 
                              className="btn btn-outline-primary btn-sm" 
                              type="button" 
                              onClick={(e) => {
                                e.stopPropagation();
                                copyUrl(counter.ownerUrl, true);
                              }}
                              aria-label={t('counter.ownerCopyButtonAriaLabel', 'Copy owner URL to clipboard')}
                            >
                              <i className="bi bi-clipboard" aria-hidden="true"></i>
                            </button>
                          </div>
                        </td>
                        )}
                        <td className="align-middle text-end">
                          <div className="d-flex justify-content-end gap-1">
                            {/* Show owner URL link for own counters */}
                            {isLoggedIn && counter.ownerEmail === user.email ? (
                              <Link 
                                to={`/${counter.ownerUrl}`} 
                                className="btn btn-outline-primary btn-sm"
                                aria-label={t('counters.openAsOwnerAriaLabel', 'Open as owner: {{name}}', { name: counter.name })}
                              >
                                <i className="bi bi-arrow-right-circle" aria-hidden="true"></i>
                                <span className="d-none d-sm-inline ms-1">{t('common.open', 'Open')}</span>
                              </Link>
                            ) : (
                              <Link 
                                to={`/${counter._id}`} 
                                className="btn btn-outline-primary btn-sm"
                                aria-label={t('counters.openCounterAriaLabel', 'Open counter: {{name}}', { name: counter.name })}
                              >
                                <i className="bi bi-arrow-right-circle" aria-hidden="true"></i>
                                <span className="d-none d-sm-inline ms-1">{t('common.open', 'Open')}</span>
                              </Link>
                            )}
                            
                            {/* Only show separate "Open as Owner" button for admins on counters they don't own */}
                            {isAdmin && !(isLoggedIn && counter.ownerEmail === user.email) && (
                              <Link 
                                to={`/${counter.ownerUrl}`} 
                                className="btn btn-outline-secondary btn-sm"
                                aria-label={t('counters.openAsOwnerAriaLabel', 'Open as owner: {{name}}', { name: counter.name })}
                              >
                                <i className="bi bi-key" aria-hidden="true"></i>
                                <span className="d-none d-lg-inline ms-1">{t('counters.openAsOwner', 'Open as Owner')}</span>
                              </Link>
                            )}
                            
                            {/* Delete button for admins and counter owners */}
                            {(isAdmin || (isLoggedIn && counter.ownerEmail === user.email)) && (
                              isConfirmingDelete === counter._id ? (
                                <>
                                  <button 
                                    className="btn btn-sm btn-danger"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteCounter(counter._id);
                                    }}
                                    aria-label={t('counters.confirmDeleteAriaLabel', 'Confirm delete counter: {{name}}', { name: counter.name })}
                                  >
                                    <i className="bi bi-check-circle d-sm-none" aria-hidden="true"></i>
                                    <span className="d-none d-sm-inline">{t('common.confirm', 'Confirm')}</span>
                                  </button>
                                  <button 
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setIsConfirmingDelete(null);
                                    }}
                                    aria-label={t('counters.cancelDeleteAriaLabel', 'Cancel delete counter')}
                                  >
                                    <i className="bi bi-x-circle d-sm-none" aria-hidden="true"></i>
                                    <span className="d-none d-sm-inline">{t('common.cancel', 'Cancel')}</span>
                                  </button>
                                </>
                              ) : (
                                <button 
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setIsConfirmingDelete(counter._id);
                                  }}
                                  aria-label={t('counters.deleteAriaLabel', 'Delete counter: {{name}}', { name: counter.name })}
                                >
                                  <i className="bi bi-trash" aria-hidden="true"></i>
                                  <span className="d-none d-md-inline ms-1">{t('counters.deleteCounter', 'Delete Counter')}</span>
                                </button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* Pagination */}
          {filteredCounters.length > 0 && (
            <Pagination 
              currentPage={currentPage}
              totalPages={totalPages}
              paginate={paginate}
              itemsPerPage={itemsPerPage}
              totalItems={filteredCounters.length}
              itemsPerPageOptions={itemsPerPageOptions}
              handleItemsPerPageChange={handleItemsPerPageChange}
              ariaLabel={t('pagination.countersListPagination', 'Counters list pagination')}
            />
          )}
        </div>
      </div>

      {/* Create Counter Modal */}
      <CreateCounterModal
        isCreating={isCreating}
        onCreateCounter={handleCreateCounter}
      />
      
      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} title={t('counters.title', 'Counters')} />
    </div>
  );
}

export default CountersList; 